// Visitor monitor store. Source of truth for what shows on the dashboard rows.

import { create } from 'zustand';
import type {
  AgentId,
  VisitorContext,
  VisitorId,
  VisitorIdentity,
  VisitorStatus,
} from '@prakash-software/shared';
import { apiFetch } from '../lib/api';

export interface SoftLockMeta {
  agentId: AgentId;
  agentName: string;
  expiresAt: number;
}

export interface VisitorRow {
  identity: VisitorIdentity;
  context: VisitorContext;
  status: VisitorStatus;
  /** Server-stamped site this visitor is on — drives the site-name label in the
   *  monitor when an agent has access to multiple sites. */
  siteId?: string;
  seenAt: number;
  /** ms when the visitor first landed in the session — drives the live timer. */
  arrivedAt: number;
  /** Set when another agent is composing the opening message (Session 2.5). */
  lock: SoftLockMeta | null;
  /** ms of the latest message on the bus (visitor OR reply). Drives recent-first sort
   *  and the unread indicator. */
  lastMessageAt?: number;
  /** Best-effort preview of the most recent message. */
  lastMessagePreview?: string;
  /** Author of the last preview-bearing message — drives the icon shown in the
   *  monitor row (visitor=incoming, ai/agent=reply). */
  lastMessageAuthor?: 'visitor' | 'ai' | 'agent';
  /** Number of visitor messages the agent hasn't acknowledged yet (incremented
   *  on each message, reset when the agent navigates to /conversation/<sid>). */
  unread: number;
}

interface VisitorsState {
  rows: Map<VisitorId, VisitorRow>;
  upsert: (row: VisitorRow) => void;
  patch: (id: VisitorId, patch: Partial<VisitorRow>) => void;
  remove: (id: VisitorId) => void;
  clear: () => void;
  setLock: (id: VisitorId, lock: SoftLockMeta | null) => void;
  /** Record a fresh message on this visitor row. Visitor messages bump
   *  unread + flip status to 'engaged'. Replies (ai / agent) don't bump
   *  unread but still update the preview + sort key. */
  noteIncoming: (
    id: VisitorId,
    at: number,
    preview: string,
    author?: 'visitor' | 'ai' | 'agent',
  ) => void;
  /** Clear the unread count for a row (agent opened the conversation). */
  markRead: (id: VisitorId) => void;
  /**
   * Agent-side edit of the visitor's identity (name / email / phone).
   * Optimistically updates local state, then persists via PATCH /api/visitors/:id.
   * On server failure, the local store is reverted so the UI stays consistent
   * with what's actually saved.
   */
  editIdentity: (
    id: VisitorId,
    patch: Partial<Pick<VisitorIdentity, 'name' | 'email' | 'phone'>>,
  ) => Promise<{ ok: true } | { ok: false; reason: string }>;
}

export const useVisitorsStore = create<VisitorsState>((set) => ({
  rows: new Map(),
  upsert: (row) =>
    set((s) => {
      const next = new Map(s.rows);
      next.set(row.identity.visitorId, row);
      return { rows: next };
    }),
  patch: (id, patch) =>
    set((s) => {
      const existing = s.rows.get(id);
      if (!existing) return s;
      const next = new Map(s.rows);
      next.set(id, { ...existing, ...patch });
      return { rows: next };
    }),
  remove: (id) =>
    set((s) => {
      const next = new Map(s.rows);
      next.delete(id);
      return { rows: next };
    }),
  clear: () => set({ rows: new Map() }),
  setLock: (id, lock) =>
    set((s) => {
      const existing = s.rows.get(id);
      if (!existing) return s;
      const next = new Map(s.rows);
      next.set(id, { ...existing, lock });
      return { rows: next };
    }),
  noteIncoming: (id, at, preview, author = 'visitor') =>
    set((s) => {
      const existing = s.rows.get(id);
      if (!existing) return s;
      const next = new Map(s.rows);
      const isVisitor = author === 'visitor';
      next.set(id, {
        ...existing,
        lastMessageAt: at,
        lastMessagePreview: preview,
        lastMessageAuthor: author,
        // Replies (ai/agent) don't bump unread.
        unread: isVisitor ? existing.unread + 1 : existing.unread,
        // Promote status only when the VISITOR speaks.
        status:
          isVisitor && existing.status !== 'departed' ? 'engaged' : existing.status,
      });
      return { rows: next };
    }),
  markRead: (id) =>
    set((s) => {
      const existing = s.rows.get(id);
      if (!existing || existing.unread === 0) return s;
      const next = new Map(s.rows);
      next.set(id, { ...existing, unread: 0 });
      return { rows: next };
    }),
  editIdentity: async (id, patch) => {
    const trimmed: Partial<Pick<VisitorIdentity, 'name' | 'email' | 'phone'>> = {};
    if (patch.name !== undefined) trimmed.name = patch.name.trim() || undefined;
    if (patch.email !== undefined) trimmed.email = patch.email.trim() || undefined;
    if (patch.phone !== undefined) trimmed.phone = patch.phone.trim() || undefined;

    // Snapshot the current identity so we can roll back on server failure.
    const before = useVisitorsStore.getState().rows.get(id)?.identity;

    // Optimistic update.
    set((s) => {
      const existing = s.rows.get(id);
      if (!existing) return s;
      const next = new Map(s.rows);
      next.set(id, {
        ...existing,
        identity: { ...existing.identity, ...trimmed },
      });
      return { rows: next };
    });

    // Persist. Server normalises empty strings to NULL.
    try {
      await apiFetch<{ visitor: unknown }>(`/api/visitors/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: trimmed.name ?? null,
          email: trimmed.email ?? null,
          phone: trimmed.phone ?? null,
        }),
      });
      return { ok: true };
    } catch (err) {
      // Roll back local state so the UI doesn't pretend the save worked.
      if (before) {
        set((s) => {
          const existing = s.rows.get(id);
          if (!existing) return s;
          const next = new Map(s.rows);
          next.set(id, { ...existing, identity: before });
          return { rows: next };
        });
      }
      const status =
        typeof err === 'object' && err !== null && 'status' in err
          ? (err as { status: number }).status
          : 0;
      return {
        ok: false,
        reason: status === 403 ? 'forbidden' : status === 400 ? 'bad-input' : 'network',
      };
    }
  },
}));

// ─── Selectors ───
// Intentionally NOT exporting `selectAllRows` — selectors that return new
// arrays each call cause infinite renders in Zustand v5 (Object.is compare).
// Read the Map (`s.rows`) at the call site and derive via `useMemo`.
